import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

const authHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';
  return { Authorization: `Bearer ${token}` };
};

const makeIdempotencyKey = action => window.crypto?.randomUUID?.() || `${action}-${Date.now()}-${Math.random()}`;

export default function CredentialDeliveryTasks({ api = '' }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [activeForm, setActiveForm] = useState(null);
  const [channel, setChannel] = useState('whatsapp');
  const [note, setNote] = useState('');
  const [revealed, setRevealed] = useState({});
  const [busyTaskId, setBusyTaskId] = useState(null);
  const timers = useRef(new Map());
  const idempotencyKeys = useRef(new Map());

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const collected = [];
      for (let offset = 0; ; offset += 500) {
        const { data } = await axios.get(`${api}/tareas-claves`, { params: { offset }, headers: authHeaders() });
        const page = Array.isArray(data) ? data : [];
        collected.push(...page);
        if (page.length < 500) break;
      }
      const ordered = collected.sort((a, b) => {
        const priority = { pending: 0, sent: 1, superseded: 2 };
        return priority[a.status] - priority[b.status] || String(b.createdAt).localeCompare(String(a.createdAt));
      });
      setTasks(ordered);
    } catch (_) {
      setError('No se pudieron cargar las tareas de entrega.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const timersForUnmount = timers.current;
    loadTasks();
    return () => {
      timersForUnmount.forEach(timer => window.clearTimeout(timer));
      timersForUnmount.clear();
    };
  }, [loadTasks]);

  useEffect(() => {
    timers.current.forEach(timer => window.clearTimeout(timer));
    timers.current.clear();
    setRevealed({});
  }, [statusFilter, serviceFilter, accountFilter, customerFilter, dateFilter]);

  const services = useMemo(() => [...new Set(tasks.map(task => task.service).filter(Boolean))].sort(), [tasks]);
  const visibleTasks = useMemo(() => tasks.filter(task =>
    (statusFilter === 'all' || task.status === statusFilter) &&
    (serviceFilter === 'all' || task.service === serviceFilter) &&
    (!accountFilter || `${task.accountEmail} ${task.accountId}`.toLowerCase().includes(accountFilter.toLowerCase())) &&
    (!customerFilter || `${task.customerName} ${task.customerPhone}`.toLowerCase().includes(customerFilter.toLowerCase())) &&
    (!dateFilter || String(task.createdAt || '').slice(0, 10) === dateFilter)
  ), [tasks, statusFilter, serviceFilter, accountFilter, customerFilter, dateFilter]);
  const pendingCount = tasks.filter(task => task.status === 'pending').length;

  const revealCredential = async task => {
    const previous = timers.current.get(task.id);
    if (previous) window.clearTimeout(previous);
    setRevealed(current => ({ ...current, [task.id]: '' }));
    try {
      const { data } = await axios.get(`${api}/inventario/${task.accountId}/credencial`, { headers: authHeaders() });
      setRevealed(current => ({ ...current, [task.id]: data.secret }));
      const timer = window.setTimeout(() => {
        setRevealed(current => ({ ...current, [task.id]: '' }));
        timers.current.delete(task.id);
      }, Math.min(Number(data.expiresInSeconds) || 15, 15) * 1000);
      timers.current.set(task.id, timer);
    } catch (_) {
      setRevealed(current => ({ ...current, [task.id]: '' }));
      setError('No se pudo revelar la clave.');
    }
  };

  const hideCredential = taskId => {
    const timer = timers.current.get(taskId);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(taskId);
    setRevealed(current => ({ ...current, [taskId]: '' }));
  };

  const copyCredential = async taskId => {
    const value = revealed[taskId];
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (_) {
      setError('No se pudo copiar la clave.');
    }
  };

  const openWhatsApp = task => {
    const phone = String(task.customerPhone || '').replace(/\D/g, '');
    if (!phone) {
      setError('Este cliente no tiene un teléfono válido para WhatsApp.');
      return;
    }
    const message = `Hola ${task.customerName || ''}. Te compartimos una actualización de acceso de ${task.service}. Confirma cuando la recibas.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const submitAction = async task => {
    const action = activeForm?.action;
    if (!action || busyTaskId) return;
    if (action === 'reopen' && !note.trim()) return;
    setBusyTaskId(task.id);
    setError('');
    try {
      const isSent = action === 'sent';
      const signature = JSON.stringify({ taskId: task.id, action, channel, note });
      const prior = idempotencyKeys.current.get(task.id);
      const idempotencyKey = prior?.signature === signature ? prior.key : makeIdempotencyKey(`${action}-${task.id}`);
      idempotencyKeys.current.set(task.id, { signature, key: idempotencyKey });
      await axios.post(`${api}/tareas-claves/${task.id}/${isSent ? 'marcar-enviada' : 'reabrir'}`,
        isSent ? { canal: channel, nota: note.trim() } : { motivo: note.trim() },
        { headers: { ...authHeaders(), 'Idempotency-Key': idempotencyKey } });
      idempotencyKeys.current.delete(task.id);
      hideCredential(task.id);
      setActiveForm(null);
      setNote('');
      await loadTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo actualizar la tarea.');
    } finally {
      setBusyTaskId(null);
    }
  };

  return (
    <section className="card shadow-sm border-0 my-4" aria-labelledby="credential-tasks-title">
      <div className="card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h5 id="credential-tasks-title" className="mb-1">Entregas de claves</h5>
          <span className="small text-muted">{pendingCount} pendientes · los mensajes se envían manualmente</span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <label className="visually-hidden" htmlFor="credential-task-account">Filtrar por cuenta</label>
          <input id="credential-task-account" className="form-control form-control-sm" placeholder="Cuenta" value={accountFilter} onChange={event => setAccountFilter(event.target.value)} />
          <label className="visually-hidden" htmlFor="credential-task-customer">Filtrar por cliente</label>
          <input id="credential-task-customer" className="form-control form-control-sm" placeholder="Cliente o teléfono" value={customerFilter} onChange={event => setCustomerFilter(event.target.value)} />
          <label className="visually-hidden" htmlFor="credential-task-date">Filtrar por fecha</label>
          <input id="credential-task-date" type="date" className="form-control form-control-sm" aria-label="Filtrar por fecha" value={dateFilter} onChange={event => setDateFilter(event.target.value)} />
          <label className="visually-hidden" htmlFor="credential-task-status">Estado</label>
          <select id="credential-task-status" className="form-select form-select-sm" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
            <option value="all">Todos los estados</option><option value="pending">Pendientes</option><option value="sent">Enviadas</option><option value="superseded">Obsoletas</option>
          </select>
          <label className="visually-hidden" htmlFor="credential-task-service">Servicio</label>
          <select id="credential-task-service" className="form-select form-select-sm" value={serviceFilter} onChange={event => setServiceFilter(event.target.value)}>
            <option value="all">Todos los servicios</option>{services.map(service => <option key={service} value={service}>{service}</option>)}
          </select>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadTasks}>Actualizar</button>
        </div>
      </div>
      {error && <div className="alert alert-danger m-3" role="alert">{error}</div>}
      {loading ? <div className="p-3 text-muted">Cargando tareas…</div> : visibleTasks.length === 0 ?
        <div className="p-3 text-muted">No hay tareas para este filtro.</div> :
        <div className="list-group list-group-flush">
          {visibleTasks.map(task => {
            const secret = revealed[task.id];
            const readOnly = task.status === 'superseded';
            return <article className="list-group-item py-3" key={task.id}>
              <div className="d-flex flex-wrap justify-content-between gap-2">
                <div>
                  <strong>{task.customerName}</strong> · {task.customerPhone || 'Sin teléfono'}<br />
                  <span className="text-muted">{task.service} · {task.accountEmail}</span><br />
                  <span className="small">Perfiles: {(task.profiles || []).map(profile => profile.label).join(', ') || '—'}</span><br />
                  <span className="small text-muted">Creada: {task.createdAt ? dayjs(task.createdAt).format('DD/MM/YYYY HH:mm') : '—'}</span>
                  {task.reason && <div className="small text-muted">Motivo: {task.reason}</div>}
                  {task.status === 'sent' && <div className="small text-muted">Enviada {task.sentAt ? dayjs(task.sentAt).format('DD/MM/YYYY HH:mm') : ''}{task.sentChannel ? ` · ${task.sentChannel}` : ''}</div>}
                </div>
                <div className="d-flex flex-wrap align-items-start gap-2">
                  <span className={`badge ${task.status === 'pending' ? 'bg-warning text-dark' : task.status === 'sent' ? 'bg-success' : 'bg-secondary'}`}>
                    {task.status === 'pending' ? 'Pendiente' : task.status === 'sent' ? 'Enviada' : 'Obsoleta'}
                  </span>
                  {!readOnly && task.status === 'pending' && <>
                    {!secret ? <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => revealCredential(task)}>Ver clave</button> : <>
                      <code aria-label="Clave temporal">{secret}</code>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyCredential(task.id)}>Copiar clave</button>
                      <button type="button" className="btn btn-sm btn-link" onClick={() => hideCredential(task.id)}>Ocultar</button>
                    </>}
                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => openWhatsApp(task)}>Abrir WhatsApp</button>
                    <button type="button" className="btn btn-sm btn-success" onClick={() => { setActiveForm({ taskId: task.id, action: 'sent' }); setChannel('whatsapp'); setNote(''); }}>Marcar enviada</button>
                  </>}
                  {!readOnly && task.status === 'sent' && <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => { setActiveForm({ taskId: task.id, action: 'reopen' }); setNote(''); }}>Reabrir</button>}
                </div>
              </div>
              {activeForm?.taskId === task.id && <div className="border rounded p-3 mt-3" role="dialog" aria-label={activeForm.action === 'sent' ? 'Registrar entrega' : 'Reabrir tarea'}>
                {activeForm.action === 'sent' ? <>
                  <label className="form-label" htmlFor={`delivery-channel-${task.id}`}>Canal de entrega</label>
                  <select id={`delivery-channel-${task.id}`} className="form-select mb-2" value={channel} onChange={event => setChannel(event.target.value)}>
                    <option value="whatsapp">WhatsApp</option><option value="call">Llamada</option><option value="other">Otro</option>
                  </select>
                  <label className="form-label" htmlFor={`delivery-note-${task.id}`}>Nota (opcional; no escribas claves)</label>
                  <input id={`delivery-note-${task.id}`} className="form-control mb-2" maxLength={500} value={note} onChange={event => setNote(event.target.value)} />
                  <button type="button" className="btn btn-primary" disabled={busyTaskId === task.id} onClick={() => submitAction(task)}>Confirmar envío</button>
                </> : <>
                  <label className="form-label" htmlFor={`reopen-note-${task.id}`}>Motivo para reabrir</label>
                  <input id={`reopen-note-${task.id}`} className="form-control mb-2" maxLength={500} value={note} onChange={event => setNote(event.target.value)} />
                  <button type="button" className="btn btn-warning" disabled={!note.trim() || busyTaskId === task.id} onClick={() => submitAction(task)}>Confirmar reapertura</button>
                </>}
                <button type="button" className="btn btn-link" onClick={() => setActiveForm(null)}>Cancelar</button>
              </div>}
            </article>;
          })}
        </div>}
    </section>
  );
}
